/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import config from '@linc-technologies/linced-common/utils/config';
import reframe from 'reframe.js';
import { get, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { observes } from '@ember-decorators/object';
// External imports
import CommentModerationStatus from '@linc-technologies/linced-common/enums/comment-moderation-status';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import PageType from '@linc-technologies/linced-common/enums/page-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import ReactionTypes from '@linc-technologies/linced-common/enums/reaction-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';
import { sortTags } from '@linc-technologies/linced-common/utils/tags';

export default class SinglePost extends Component {
  // Component versioning
  @tracked visualStyle = 'v1';

  // Attrs
  PageType = PageType;

  PostStatus = PostStatus;

  PostType = PostType;

  schoolColor = 'teal';

  // Services
  @service
  inViewport;

  @service
  notifications;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Props
  // editPost provides a callback function to edit the selected post.
  editPost() {}

  // setFilterTag provides a callback function to filter posts based on the
  // selected goal or tag.
  setFilterTag() {}

  // addAuthor is a callback for filtering on author.
  addAuthor() {}

  // addGroup is a callback for filtering on group.
  addGroup() {}

  // trackReadState controls whether this post should watch for itself being
  // read/viewed by the user.
  trackReadState = false;

  // disableActions allows disabling the actions menu, for a "read-only" list.
  disableActions = false;

  // avatar allows controlling whose avatar is shown on the post.
  // Allowed options: ['person', 'author']
  @tracked avatar = 'author';

  // topicLabel is the singular topic label of the post school.
  @computed('post.school')
  get topicLabel() {
    return (
      this.options.schoolGet(this.post.school, 'core:labels:topic') || 'Topic'
    );
  }

  // avatarResource determines which avatar image to show.
  @computed('post.{postType,school,person,author}', 'avatar')
  get avatarResource() {
    switch (this.post.postType) {
      case PostType.COMMUNITY_NOTICE:
        return this.post.school;

      default:
        if (this.avatar === 'person' && !isEmpty(this.post.person)) {
          return this.post.person;
        }
        if (!isEmpty(this.post.author)) {
          return this.post.author;
        }
    }

    return null;
  }

  // avatarCaption determines the name to use as the title/alt text on the avatar.
  @computed(
    'post.{postType,school.name,person.fullName,author.fullName}',
    'avatar',
  )
  get avatarCaption() {
    switch (this.post.postType) {
      case PostType.GLOBAL_NOTICE:
        return 'Hero Team';

      case PostType.COMMUNITY_NOTICE:
        return this.get('post.school.name');

      default:
        if (this.avatar === 'person' && !isEmpty(this.post.person)) {
          return this.get('post.person.fullName');
        }
        if (!isEmpty(this.post.author)) {
          return this.get('post.author.fullName');
        }
    }
  }

  @computed('post.postType')
  get avatarUrl() {
    if (this.post.postType === PostType.GLOBAL_NOTICE) {
      return '/assets/images/icons/hero.png';
    }

    return null;
  }

  // isAdminApp determines whether this is being rendered by the admin app.
  @tracked isAdminApp = config.modulePrefix === 'linced-admin';

  // authorName determines the author name to show in the byline.
  @computed('post.{postType,author.fullName}', 'isAdminApp')
  get authorName() {
    switch (this.post.postType) {
      case PostType.GLOBAL_NOTICE:
        if (this.isAdminApp) {
          // Show LINC-ED staff who actually wrote the post
          // when viewing in the admin app.
          return this.get('post.author.fullName');
        }
        return 'Hero Team';

      default:
        return this.get('post.author.fullName');
    }
  }

  // Pick the first group so that we can pass this through to the link for showing the topic details from a community notice
  // This computed property returns the first group from post.groups
  @computed('post.groups')
  get firstGroup() {
    let groups = this.get('post.groups');

    if (Array.isArray(groups)) {
      return groups.length > 0 ? groups[0] : null; // Return the first group in the array or null if the array is empty
    } else if (typeof groups === 'object' && groups !== null) {
      return groups; // If groups is a single object, return it directly
    } else {
      return null; // Handle cases where groups is null, undefined, or not a valid type
    }
  }

  // showGroups determines whether to show group chips with the byline.
  @computed('post.postType', 'isAdminApp')
  get showGroups() {
    if (this.get('post.postType') === PostType.GLOBAL_NOTICE) {
      // Only show groups on Hero/Global Notices within the admin app.
      return this.isAdminApp;
    }
    return [
      PostType.COMMUNITY_NOTICE,
      PostType.STUDENT_NOTICE,
      PostType.TEXT,
    ].includes(this.post.postType);
  }

  // showHeroNoticesButton determines whether to show the user a button which
  // will take them to the list of Hero Notices when clicked.
  @computed('post.postType', 'isAdminApp', 'router.currentRouteName')
  get showHeroNoticesButton() {
    if (this.isAdminApp) {
      // Don't show the button in the admin app.
      return false;
    }
    if (this.get('post.postType') !== PostType.GLOBAL_NOTICE) {
      // Don't show if not a Hero/Global notice.
      return false;
    }
    // Only show the button on the Staff Feed.
    return this.get('router.currentRouteName') === 'home.daily-notices.index';
  }

  // prerenderComments determines whether we should prerender comments
  // so that they'll all be included if the page is printed.
  prerenderComments = true;

  // State
  @tracked
  activeMedia = null;

  commentsExpanded = false;

  @tracked
  embeddedMedia = [];

  @tracked
  files = [];

  @tracked
  isRead = false;

  topSentinelSeen = false;

  bottomSentinelSeen = false;

  // Methods
  init() {
    super.init(...arguments);
    this.updateMedia();
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.initReadStateTracker();
  }

  initReadStateTracker() {
    if (this.isRead || !this.trackReadState) {
      // Don't track read state for already-read posts, or those with tracking disabled.
      return;
    }

    let top = this.element.querySelector('.post-view-sentinel-top');
    let bottom = this.element.querySelector('.post-view-sentinel-bottom');
    if (!isEmpty(top) && !isEmpty(bottom)) {
      const { onEnter: onEnterTop } = this.inViewport.watchElement(top);
      onEnterTop(this.didEnterViewport.bind(this, 'top'));

      const { onEnter: onEnterBottom } = this.inViewport.watchElement(bottom);
      onEnterBottom(this.didEnterViewport.bind(this, 'bottom'));
    }
  }

  deinitReadStateTracker() {
    // Ignore if we don't have an element (being destroyed?)
    if (isEmpty(this.element)) {
      return;
    }
    // Stop watching sentinels for being in the viewport
    let top = this.element.querySelector('.post-view-sentinel-top');
    if (!isEmpty(top)) {
      this.inViewport.stopWatching(top);
    }
    let bottom = this.element.querySelector('.post-view-sentinel-bottom');
    if (!isEmpty(bottom)) {
      this.inViewport.stopWatching(bottom);
    }
  }

  didEnterViewport(sentinel) {
    let key = `${sentinel}SentinelSeen`;
    let otherKey;
    switch (sentinel) {
      case 'top':
        otherKey = 'bottomSentinelSeen';
        break;
      case 'bottom':
        otherKey = 'topSentinelSeen';
    }

    let thisSeen = get(this, key);
    let otherSeen = get(this, otherKey);

    if (thisSeen && otherSeen) {
      // Both sentinels have already been seen; this should have already been processed.
      return;
    }

    // Mark this sentinel as seen, for future checks.
    this.set(`${sentinel}SentinelSeen`, true);

    if (otherSeen) {
      // We've already seen the other sentinel, so this post is now "read".
      this.notifications.markEntityRead(this.post.id);
      this.isRead = true;

      // We don't need to watch for this post entering the viewport any more.
      this.deinitReadStateTracker();
    }
  }

  destroy() {
    // NOTE: willDestroy sounds as though it'd be the place to do this,
    // but it doesn't fire early enough - destroy actually gets called first.
    this.deinitReadStateTracker();
    return super.destroy(...arguments);
  }

  // canEditPost returns true if the current user can edit the post.
  // Note: The author of a post doesn’t change, so we’re depending on the post ID instead of the author
  // in order to avoid issues if the author’s Person record doesn’t exist. Ember complains about being
  // unable to create a chain watcher on a record after it’s destroyed, which seems to be because a stub
  // record is created in the local store and then removed when a 404 response is received.
  @computed(
    'session.{personId,scopes.[]}',
    'post.{author.id,postType,page.staffCanEdit,status}',
  )
  get canEditPost() {
    // Administrators can edit any post
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    if (this.permissions.meet(Scopes.TENANT_STAFF)) {
      if (this.post.get('postType') === PostType.STAFF_NOTICE) {
        // Staff can only edit published notices, or their own
        return (
          this.get('post.author.id') === this.get('session.personId') ||
          this.post.get('status') === PostStatus.PUBLISHED
        );
      }
      if (
        this.post.get('postType') !== PostType.POST ||
        this.get('post.page.staffCanEdit')
      ) {
        return true;
      }
    }
    // Other users can edit their own posts only
    return this.get('post.author.id') === this.get('session.personId');
  }

  @computed('post.{author.type}')
  get isStudentPost() {
    return this.get('post.author.type') === PersonType.LEARNER;
  }

  @computed(
    'session.{personId,scopes.[]}',
    'post.{id,isSpecialUse,status}',
    'canManageExpulsions',
    'isStudentPost',
  )
  get canDeletePost() {
    // Special use posts can only be deleted if they have not been published
    if (this.get('post.isSpecialUse')) {
      let postHasBeenPublished =
        this.post.status === PostStatus.PUBLISHED ||
        this.post.status === PostStatus.UNPUBLISHED;
      if (postHasBeenPublished) {
        return false;
      }
      // Otherwise, only a principal or principals delegate can delete
      // special use posts
      return this.canManageExpulsions;
    }
    // Administrators can delete any post
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    if (this.permissions.meet(Scopes.TENANT_STAFF)) {
      if (this.isStudentPost) {
        return true;
      }
    }
    // Other users can delete their own posts only
    return this.get('post.author.id') === this.get('session.personId');
  }

  @computed('session.{roles.[],scopes.[]}')
  get canManageExpulsions() {
    return (
      this.roles.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE) ||
      this.permissions.meet(Scopes.LINCED_ADMIN)
    );
  }

  // canPublishPost calculates whether the current user is able to publish a
  // given post.
  // Note: see note above re: post.id dependent key
  @computed(
    'session.{personId,scopes.[]}',
    'post.{id,page.visibleToParents,page.visibleToLearners,postType,isSpecialUse}',
  )
  get canPublishPost() {
    // Base case:
    // - Must be at least a school staff member in order to be able to
    //   publish any post.
    let userIsStaff = this.permissions.meet(Scopes.TENANT_STAFF);
    let userIsAdmin = this.permissions.meet(Scopes.TENANT_ADMIN);

    if (this.get('post.isSpecialUse')) {
      if (
        this.get('post.page.isBehaviour') ||
        this.get('post.page.isSuspensionPosts') ||
        this.get('post.page.isSuspensionLetters')
      ) {
        if (!this.canManageExpulsions) {
          return false;
        }
      }
    }

    if (!userIsStaff) {
      return false;
    }

    // compute post publish~ability based on type.
    let post = this.post;
    let visibleToParents = false;
    let visibleToLearners = false;
    switch (post.postType) {
      case PostType.STAFF_NOTICE:
        // staff notices are automatically published.
        return false;

      case PostType.POST:
        // Check post visibility status.
        visibleToParents = this.get('post.page.visibleToParents');
        visibleToLearners = this.get('post.page.visibleToLearners');
        if (!(visibleToParents || visibleToLearners)) {
          return false;
        }
    }

    // Administrators can publish any post
    if (userIsAdmin) {
      return true;
    }

    // Teachers can publish any post by a student
    let authorIsStudent = this.get('post.author.type') === PersonType.LEARNER;
    if (authorIsStudent && userIsStaff) {
      return true;
    }

    // Check page permissions
    if (this.get('post.page.staffCanPublish')) {
      // Staff can publish anyone's posts
      return true;
    }

    // By default, school staff can publish their own posts only
    return this.get('post.author.id') === this.get('session.personId');
  }

  @computed('post.{postType,page.canComment}')
  get commentsEnabled() {
    switch (this.get('post.postType')) {
      case PostType.COMMUNITY_NOTICE:
        return this.get('post.school.communityFeedCommentsEnabled');

      case PostType.POST:
        // Controlled by the Page for regular Posts.
        return this.get('post.page.canComment');

      case PostType.ATTENDANCE_NOTE:
      case PostType.GLOBAL_NOTICE:
        // No comments on these types of post.
        return false;

      default:
        // Notice types can be commented on.
        return true;
    }
  }

  @computed('post.{postType,page.reactionType}')
  get reactionType() {
    switch (this.get('post.postType')) {
      case PostType.COMMUNITY_NOTICE:
      case PostType.STUDENT_NOTICE:
        return ReactionTypes.EMOJI;
      default:
        return this.get('post.page.reactionType');
    }
  }

  @computed('post.page.tagCategories.[]')
  get tagCategoryIds() {
    // post.page is an async belongsTo (a PromiseBelongsTo): native property
    // access on it asserts, so use classic get(). tagCategories itself is
    // already resolved to a plain array by the Page model.
    return (this.get('post.page.tagCategories') ?? []).map(
      (category) => category.id,
    );
  }

  @computed('post.{tags.length,goals.length}')
  get hasTagsOrGoals() {
    return this.post.tags.length > 0 || this.post.goals.length > 0;
  }

  @tracked
  commentText;

  @action
  setCommentText(value) {
    this.commentText = value;
  }

  @tracked
  deleteDialogOpen;

  @tracked
  deleteFailed;

  @tracked
  deleteInProgress;

  @tracked
  replyingTo;

  @computed('post.tags.[]', 'tagCategoryIds')
  get filteredTags() {
    let categoryIds = this.tagCategoryIds;
    // post.tags is an async hasMany (a PromiseManyArray): native property
    // access on it asserts, so use classic get() to reach its content.
    return (this.get('post.tags.content') ?? []).filter((tag) => {
      return categoryIds.includes(tag.get('category.id'));
    });
  }

  @computed('filteredTags.[]', 'tagCategoryIds.[]')
  get sortedTags() {
    return sortTags(this.filteredTags, this.tagCategoryIds);
  }

  @observes('post.media.content.@each.{mediaType,mimeType}')
  _handleMedia() {
    this.updateMedia();
  }

  // canModerateComments calculates whether a user can moderate comments under this post,
  // based on the permissions rule set
  // enables moderation buttons
  @computed('post.author.id', 'session.{personId,scopes.[]}')
  get canModerateComments() {
    // Ruleset:
    // - Allow LINC-ED Admins and Managers to moderate any community notice comment made by a Caregiver.
    // - Allow Tenant Admins to moderate any community notice comment made by a Caregiver.
    // - Allow Tenant Staff to moderate any community notice comment made by a Caregiver on the Staff's
    // post.
    // - Deny Caregivers, Learners from moderating any community notice comments.
    // - Deny all.

    if (this.post.get('postType') !== PostType.COMMUNITY_NOTICE) {
      // Comment moderation is only enabled in community notices
      return false;
    }
    // LINC-ED managers and admins, tenant admins
    if (
      this.permissions.meet(Scopes.LINCED_MANAGER) ||
      this.permissions.equal(Scopes.TENANT_ADMIN)
    ) {
      return true;
    }

    // Tenant staff who is the post author
    let postAuthorId = this.post.get('author.id');
    let personId = this.get('session.personId');
    if (!isEmpty(postAuthorId)) {
      let isStaff = this.permissions.equal(Scopes.TENANT_STAFF);
      return isStaff && postAuthorId === personId;
    }

    // Anyone else is not allowed
    return false;
  }

  @computed('post.comments.content.@each.{moderationStatus,content}')
  get pendingComments() {
    return resolved(this.post.get('comments')).filter((comment) => {
      return (
        // Count pending comments without including removed comments that could have the status
        comment.get('moderationStatus') ===
          CommentModerationStatus.UNSPECIFIED &&
        !isEmpty(comment.get('content'))
      );
    });
  }

  async updateMedia() {
    let media = await this.post.media;
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    let embeddable = media.filter(function (file) {
      return (
        // Embed images for everyone
        file.get('mediaType') === MediaType.IMAGE ||
        // Embed videos for everyone
        file.get('mediaType') === MediaType.VIDEO ||
        // Embed "embeds" for everyone - mostly Google Drive / YouTube,
        // which handle browser support for us.
        file.get('mediaType') === MediaType.EMBED || // Embed PDFs
        file.get('mimeType') === 'application/pdf'
      );
    });
    let files = media.filter(function (file) {
      return !embeddable.includes(file);
    });

    this.files = files;

    if (embeddable.length) {
      this.embeddedMedia = embeddable;
      this.activeMedia = embeddable[0];
    } else {
      this.embeddedMedia = [];
      this.activeMedia = null;
    }
  }

  @action
  viewPost(postId) {
    let postType = this.post.get('postType');
    switch (postType) {
      case PostType.COMMUNITY_NOTICE:
        this.router.transitionTo(`/home/community-notices/${postId}`);
        break;
      case PostType.STUDENT_NOTICE:
        this.router.transitionTo(`/home/student-notices/${postId}`);
        break;
      default:
        this.router.transitionTo(`/home/community-notices/${postId}`);
        break;
    }
  }

  @action
  toggleComments() {
    this.toggleProperty('commentsExpanded');
  }

  @action
  replyToComment(comment) {
    this.replyingTo = comment;
  }

  @action
  cancelReply() {
    this.replyingTo = null;
  }

  @action
  comment() {
    let post = this.post;
    let postType = post.get('postType');
    let record = this.store.createRecord('posts/comment', {
      schoolId: this.get('session.activeSchool'),
      commentId: this.replyingTo,
      content: this.commentText,
      authorId: this.get('session.person'),
      postType,
    });
    switch (postType) {
      case PostType.COMMUNITY_NOTICE:
        record.set('communityNoticeId', post);
        break;
      case PostType.STUDENT_NOTICE:
        record.set('studentNoticeId', post);
        break;
      case PostType.STAFF_NOTICE:
        record.set('staffNoticeId', post);
        break;
      case PostType.TEXT:
        record.set('textId', post);
        break;
      default:
        record.set('postId', post);
        break;
    }

    // A failed comment is discarded; commentText keeps it for a retry.
    saveOrDiscard(record)
      .then(() => {
        this.replyingTo = null;
        this.commentText = '';
      })
      .catch(() => this.toaster.error('Error posting the comment.'));
  }

  @action
  edit() {
    this.editPost(this.post);
  }

  @action
  delete() {
    this.deleteInProgress = true;
    this.deleteFailed = false;
    let person = this.post.get('person');

    this.post
      .destroyRecord()
      .then(() => {
        this.toaster.success(`Post deleted successfully`);
        if (person) {
          person.then((record) => {
            if (!isEmpty(record)) {
              record.reload();
            }
          });
        }
      })
      .catch(() => {
        this.deleteFailed = true;
        this.toaster.error(`Error removing the post, please try again`);
      })
      .finally(() => {
        this.deleteInProgress = false;
        this.deleteDialogOpen = false;
      });
  }

  @action
  editComment(comment, updatedContent) {
    this.store
      .findRecord('posts/comment', comment.id)
      .then((currentComment) => {
        currentComment.set('content', updatedContent);
        // Need to return it to catch possible errors
        return currentComment.save();
      })
      .then(() => {
        this.toaster.success(`Comment updated`);
      })
      .catch((error) => {
        if (error.errors[0].status === '403') {
          this.toaster.error(`You are not authorized to update this comment`);
        } else {
          this.toaster.error(`Error updating comment, please try again`);
        }
      });
  }

  @action
  deleteComment(comment) {
    // TODO: decide on how we implement deletion on the backend due to
    //  permission issues surrounding cascading reply deletion for teachers,
    //  caregiver and learners.
    this.store
      .findRecord('posts/comment', comment.id, { backgroundReload: false })
      .then((record) => {
        record.set('content', '');
        // Need to return it to catch possible errors
        return record.save();
      })
      .then(() => {
        this.toaster.success(`Comment removed`);
      })
      .catch((error) => {
        if (error.errors[0].status === '403') {
          this.toaster.error(`You are not authorized to delete this comment`);
        } else {
          this.toaster.error(`Error removing comment, please try again`);
        }
      });
  }

  @action
  handleSetFilterTag(tag) {
    this.setFilterTag(tag);
  }

  @action
  handleSetFilter(filterQuery, filterLabel, filterType) {
    this.setFilter(filterQuery, filterLabel, filterType);
  }

  // Lifecycle Hooks
  didRender() {
    super.didRender(...arguments);
    reframe(`#${this.elementId} .post-body iframe`);
  }
}
